import { useQuery } from '@tanstack/react-query'
import { socMetricsQuery, type SocMetricStat } from '../api/securitee'
import './soc-metrics.css'

/** 420 -> "7 min", 4500 -> "1 h 15 min", 190000 -> "2 d 4 h". */
export function duration(seconds: number): string {
  if (seconds < 60) return '<1 min'
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    return m ? `${h} h ${m} min` : `${h} h`
  }
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  return h ? `${d} d ${h} h` : `${d} d`
}

const percent = (rate: number) => `${Math.round(rate * 100)}%`

type Card = { title: string; value: string; note: string; tone?: 'alert' | 'ok' }

function timeCard(title: string, stat: SocMetricStat): Card {
  if (stat.median === null || stat.p90 === null) return { title, value: '-', note: `Too few tickets yet (${stat.count})` }
  return { title, value: duration(stat.median), note: `90% within ${duration(stat.p90)} · ${stat.count} tickets` }
}

/**
 * Masurile cozii SOC deasupra Incidents (docs XDR 03, decizia 11, discutate 20
 * oct): preluare si rezolvare (mediana + p90), fals pozitive, tichete neatinse
 * de peste o ora, si regulile cu cele mai multe fals pozitive. Fara drept sau
 * cu eroare: nimic - lista de dedesubt ramane. Starea "neatinse" e si in text,
 * nu numai in culoare.
 */
export function SocMetricsStrip() {
  const q = useQuery(socMetricsQuery())
  const m = q.data
  if (!m) return null

  const fp = m.false_positives
  const cards: Card[] = [
    timeCard('Time to pick up', m.mtta),
    timeCard('Time to resolve', m.mttr),
    fp.rate === null
      ? { title: 'False positives', value: '-', note: 'No incidents yet' }
      : { title: 'False positives', value: percent(fp.rate), note: `${fp.false_positives} of ${fp.incidents} incidents` },
    m.untouched > 0
      ? { title: 'Untouched > 1 h', value: String(m.untouched), note: 'Needs a person now', tone: 'alert' }
      : { title: 'Untouched > 1 h', value: '0', note: 'All picked up', tone: 'ok' },
  ]

  return (
    <section className="soc-metrics" aria-label={`SOC metrics, last ${m.window_days} days`}>
      <div className="soc-metrics__grid">
        {cards.map((c) => (
          <dl className={`soc-metric${c.tone ? ` soc-metric--${c.tone}` : ''}`} key={c.title}>
            <dt className="soc-metric__title">{c.title}</dt>
            <dd className="soc-metric__value">{c.value}</dd>
            <dd className="soc-metric__note">{c.note}</dd>
          </dl>
        ))}
      </div>
      <p className="soc-metrics__foot">
        Last {m.window_days} days. Picked up = the first time a person assigned the ticket; resolved = the first time it
        reached Resolved or Closed.
      </p>
      {m.noisy_rules.length > 0 && (
        <details className="soc-noisy-rules">
          <summary>{`Noisiest rules (${m.noisy_rules.length})`}</summary>
          <table className="soc-noisy-rules__table">
            <thead>
              <tr>
                <th scope="col">Rule</th>
                <th scope="col">ID</th>
                <th scope="col">False positives</th>
                <th scope="col">Rate</th>
              </tr>
            </thead>
            <tbody>
              {m.noisy_rules.map((r) => (
                <tr key={r.rule_id}>
                  <td>{r.name}</td>
                  <td>{r.rule_id}</td>
                  <td>{`${r.false_positives} of ${r.incidents}`}</td>
                  <td>{percent(r.rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </section>
  )
}
